// ESTE FILTRO CUMPLE LA MISMA FUNCION PROMOCIONES MULTIPLES SELECCIONES , ACTUALIZAR A ESTE BORRAR EL OTRO ARCHIVO 

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputBase,
  Paper,
  Popper,
  Radio,
  Typography
} from '@mui/material'
import GoogleIcon from '../../icon/GoogleIcon'

const DEFAULT_FILTER_VALUE = {
  mode: 'all',
  values: []
}

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

const MuiGroupedFilterSelect = ({
  title,
  groups = [],
  value = DEFAULT_FILTER_VALUE,
  onChange,
  disabled = false,
  searchable = true,
  width = 290,
  dropdownWidth = 390,
  searchPlaceholder = 'Palabras claves...',
  groupLabel = 'Filtrar por:'
}) => {
  const anchorRef = useRef(null)
  const searchInputRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedGroupKey, setSelectedGroupKey] = useState('')

  const selectedValues = value?.mode === 'custom' ? value.values || [] : []
  const selectedValuesSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const selectedCount = selectedValues.length
  const isRaised = open

  const normalizedGroups = useMemo(() => {
    return groups.filter((group) => Array.isArray(group.options) && group.options.length > 0)
  }, [groups])

  const activeGroupKey = useMemo(() => {
    if (selectedGroupKey && normalizedGroups.some((group) => group.key === selectedGroupKey)) {
      return selectedGroupKey
    }

    return normalizedGroups[0]?.key || ''
  }, [normalizedGroups, selectedGroupKey])

  const activeGroup = useMemo(() => {
    return normalizedGroups.find((group) => group.key === activeGroupKey) || null
  }, [activeGroupKey, normalizedGroups])

  const filteredOptions = useMemo(() => {
    const options = activeGroup?.options || []
    const normalizedSearch = normalizeText(search)

    if (!normalizedSearch) return options

    return options.filter((option) => normalizeText(option.label).includes(normalizedSearch))
  }, [activeGroup, search])

  useEffect(() => {
    if (!normalizedGroups.length) {
      setSelectedGroupKey('')
      return
    }

    if (!selectedGroupKey || !normalizedGroups.some((group) => group.key === selectedGroupKey)) {
      setSelectedGroupKey(normalizedGroups[0].key)
    }
  }, [normalizedGroups, selectedGroupKey])

  useEffect(() => {
    if (open && searchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)

      return () => clearTimeout(timer)
    }
  }, [open, searchable])

  const handleToggleOpen = () => {
    if (disabled) return
    setOpen((prev) => !prev)
  }

  const handleClose = () => {
    setOpen(false)
    setSearch('')
  }

  const handleSelectGroup = (groupKey) => {
    setSelectedGroupKey(groupKey)
    setSearch('')
  }

  const handleToggleOption = (optionValue) => {
    if (selectedValuesSet.has(optionValue)) {
      const nextValues = selectedValues.filter((valueItem) => valueItem !== optionValue)

      if (!nextValues.length) {
        onChange({
          mode: 'all',
          values: []
        })
        return
      }

      onChange({
        mode: 'custom',
        values: nextValues
      })
      return
    }

    onChange({
      mode: 'custom',
      values: [...selectedValues, optionValue]
    })
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box className="mui-filter-select" style={{ width }}>
        <Box
          ref={anchorRef}
          onClick={handleToggleOpen}
          className={`mui-filter-select__trigger ${open ? 'mui-filter-select__trigger--open' : ''} ${disabled ? 'mui-filter-select__trigger--disabled' : ''}`}
        >
          {isRaised ? (
            <Box className="mui-filter-select__raised-label">
              <Typography className="mui-filter-select__raised-title">{title}</Typography>

              {selectedCount > 0 && (
                <Typography className="mui-filter-select__raised-count">
                  ({selectedCount})
                </Typography>
              )}
            </Box>
          ) : null}

          <Box className="mui-filter-select__content">
            <Box
              className={`mui-filter-select__label-wrap ${open ? 'mui-filter-select__label-wrap--hidden' : ''}`}
            >
              <Typography className="mui-filter-select__title">{title}</Typography>

              {selectedCount > 0 && (
                <Typography className="mui-filter-select__count">({selectedCount})</Typography>
              )}
            </Box>

            <GoogleIcon
              name={open ? 'arrow_drop_up' : 'arrow_drop_down'}
              size={24}
              className={`mui-filter-select__icon ${open ? 'mui-filter-select__icon--open' : ''}`}
            />
          </Box>
        </Box>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className="mui-filter-select__popper"
        >
          <Paper
            elevation={6}
            className="mui-filter-select__paper mui-grouped-filter-select__paper"
            style={{ width: dropdownWidth }}
          >
            <Typography className="mui-grouped-filter-select__section-title">
              {groupLabel}
            </Typography>

            <Box className="mui-grouped-filter-select__groups">
              {normalizedGroups.map((group) => (
                <Box
                  key={group.key}
                  onClick={() => handleSelectGroup(group.key)}
                  className="mui-grouped-filter-select__group-option"
                >
                  <Radio
                    checked={activeGroupKey === group.key}
                    disableRipple
                    className="mui-filter-select__checkbox"
                  />

                  <Typography className="mui-filter-select__select-all-text">
                    {group.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {activeGroup && (
              <>
                <Box className="mui-grouped-filter-select__divider" />

                {searchable && (
                  <Box className="mui-filter-select__search-wrap">
                    <InputBase
                      className="mui-filter-select__search-input"
                      inputRef={searchInputRef}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={searchPlaceholder}
                      fullWidth
                    />
                  </Box>
                )}

                <Box className="mui-filter-select__options">
                  {filteredOptions.map((option) => {
                    const checked = selectedValuesSet.has(option.value)

                    return (
                      <Box
                        key={option.value}
                        onClick={() => handleToggleOption(option.value)}
                        className="mui-filter-select__option"
                      >
                        <Checkbox
                          checked={checked}
                          disableRipple
                          className="mui-filter-select__checkbox mui-filter-select__checkbox--option"
                        />

                        <Typography className="mui-filter-select__option-label">
                          {option.label}
                        </Typography>
                      </Box>
                    )
                  })}

                  {!filteredOptions.length && (
                    <Typography className="mui-filter-select__empty">No hay resultados</Typography>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default MuiGroupedFilterSelect
